/** Typical outbound-message count for DKLS/Schnorr 2-party sign (each side sends ~4). */
export const WB32COSIGN_MANUAL_MPC_ROUND_ESTIMATE = 4;

/** Max bytes for a single QR payload. Messages larger than this are split into chunks. */
export const MAX_QR_CHUNK_BYTES = 2200;

/** Chunk prefix: `M:{part}:{total}:{data}`.  Colons never appear in base64 so this is unambiguous. */
const CHUNK_PREFIX = 'M:';

/**
 * cosignTransports — Pluggable transport layer for WB32COSIGN MPC relay.
 *
 * Separates message delivery from the MPC protocol so cosigning can work
 * without depending on api.vultisig.com. Each transport implements the
 * same send/receive interface; the MPC layer doesn't care which is active.
 *
 * Available transports:
 *   - BroadcastChannelTransport  (same origin / same device, zero config)
 *   - HttpRelayTransport         (cross-device via any HTTP relay server)
 *   - ManualTransport            (airgapped copy-paste, UI-driven)
 */

// ── Interface ────────────────────────────────────────────────────────

export interface CosignTransport {
	readonly name: string;
	send(from: string, to: string, encryptedBody: string): Promise<void>;
	receive(myRole: string): Promise<string[]>;
	close(): void;
}

export type TransportType = 'broadcastchannel' | 'relay' | 'manual' | 'qr' | 'webrtc';

// ── BroadcastChannel Transport (same-origin, zero config) ────────────

export class BroadcastChannelTransport implements CosignTransport {
	readonly name = 'BroadcastChannel (local)';
	private channel: BroadcastChannel;
	private inbox: Map<string, string[]> = new Map();

	constructor(sessionId: string) {
		this.channel = new BroadcastChannel(`wb32cosign-${sessionId}`);
		this.channel.onmessage = (ev) => {
			const { to, body } = ev.data || {};
			if (!to || !body) return;
			const queue = this.inbox.get(to) || [];
			queue.push(body);
			this.inbox.set(to, queue);
		};
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		this.channel.postMessage({ from, to, body: encryptedBody });
	}

	async receive(myRole: string): Promise<string[]> {
		const queue = this.inbox.get(myRole) || [];
		if (queue.length === 0) return [];
		this.inbox.set(myRole, []);
		return queue;
	}

	close(): void {
		this.channel.close();
	}
}

// ── HTTP Relay Transport (works with any relay implementing the API) ─

const RELAY_START_RETRIES = 2;
const RATE_LIMIT_INITIAL_DELAY_MS = 2_000;
const RATE_LIMIT_MAX_DELAY_MS = 30_000;

/**
 * Parses the standard `Retry-After` header. RFC 7231 allows either an
 * integer seconds value or an HTTP-date. We accept the integer form
 * (the only one a sane rate-limiter actually produces) and clamp to
 * `RATE_LIMIT_MAX_DELAY_MS` so a misbehaving server can't stall the
 * session for arbitrary amounts of time.
 */
function parseRetryAfterSeconds(header: string | null): number | null {
	if (!header) return null;
	const n = parseInt(header.trim(), 10);
	if (!Number.isFinite(n) || n < 0) return null;
	return Math.min(n, RATE_LIMIT_MAX_DELAY_MS / 1000);
}

/**
 * Tracks an exponentially-growing "do not call before" timestamp for a
 * URL family that's been rate-limited. The same backoff window is
 * shared between `ensureSession` / `send` / `receive` because they all
 * hit the same Origin, so once we get a 429 anywhere we want every
 * call to honour the cooldown.
 */
class RateLimitBackoff {
	private notBeforeMs = 0;
	private nextDelayMs = RATE_LIMIT_INITIAL_DELAY_MS;

	/** Milliseconds to wait before the next call, or 0 if we're clear. */
	delayBeforeNext(): number {
		const remaining = this.notBeforeMs - Date.now();
		return remaining > 0 ? remaining : 0;
	}

	/** Record a 429 (or equivalent) and bump the cooldown. */
	noteRateLimited(retryAfterSeconds: number | null): void {
		const explicitMs = retryAfterSeconds !== null
			? retryAfterSeconds * 1000
			: this.nextDelayMs;
		this.notBeforeMs = Date.now() + explicitMs;
		this.nextDelayMs = Math.min(this.nextDelayMs * 2, RATE_LIMIT_MAX_DELAY_MS);
	}

	/** A successful call clears the backoff window. */
	noteOk(): void {
		this.notBeforeMs = 0;
		this.nextDelayMs = RATE_LIMIT_INITIAL_DELAY_MS;
	}
}

/** Sleep until the backoff window allows the next call. */
async function sleepForBackoff(backoff: RateLimitBackoff): Promise<void> {
	const wait = backoff.delayBeforeNext();
	if (wait > 0) {
		await new Promise((r) => setTimeout(r, wait));
	}
}

export class HttpRelayTransport implements CosignTransport {
	readonly name: string;
	private relayUrl: string;
	private sessionId: string;
	private started = false;
	private backoff = new RateLimitBackoff();

	constructor(relayUrl: string, sessionId: string) {
		this.relayUrl = relayUrl.replace(/\/+$/, '');
		this.sessionId = sessionId;
		this.name = `HTTP relay (${this.relayUrl.replace(/^https?:\/\//, '').split('/')[0]})`;
	}

	private async ensureSession(): Promise<void> {
		if (this.started) return;
		for (let attempt = 0; attempt <= RELAY_START_RETRIES; attempt++) {
			await sleepForBackoff(this.backoff);
			try {
				const resp = await fetch(`${this.relayUrl}/${this.sessionId}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify([]),
				});
				if (resp.ok || resp.status === 201 || resp.status === 409) {
					this.started = true;
					this.backoff.noteOk();
					return;
				}
				if (resp.status === 429) {
					this.backoff.noteRateLimited(parseRetryAfterSeconds(resp.headers.get('Retry-After')));
					console.warn(
						`[HttpRelay] ensureSession attempt ${attempt}: 429 — backing off for `
						+ `${this.backoff.delayBeforeNext()}ms`,
					);
					continue;
				}
				console.warn(`[HttpRelay] ensureSession attempt ${attempt}: ${resp.status}`);
			} catch (err) {
				console.warn(`[HttpRelay] ensureSession attempt ${attempt}:`, (err as Error)?.message);
			}
		}
		this.started = true;
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		await this.ensureSession();
		await sleepForBackoff(this.backoff);
		const response = await fetch(`${this.relayUrl}/message/${this.sessionId}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				session_id: this.sessionId,
				from,
				to: [to],
				body: encryptedBody,
			}),
		});
		if (response.status === 429) {
			this.backoff.noteRateLimited(parseRetryAfterSeconds(response.headers.get('Retry-After')));
			// Treat as a soft failure: the MPC layer will retry on its
			// next round-trip and the cooldown will have elapsed.
			console.warn(
				`[HttpRelay] send 429 — backing off for ${this.backoff.delayBeforeNext()}ms`,
			);
			return;
		}
		if (!response.ok) {
			const text = await response.text().catch(() => '');
			throw new Error(`Relay send failed: ${response.status} ${text}`);
		}
		this.backoff.noteOk();
	}

	async receive(myRole: string): Promise<string[]> {
		await this.ensureSession();
		await sleepForBackoff(this.backoff);
		const response = await fetch(
			`${this.relayUrl}/message/${this.sessionId}/${encodeURIComponent(myRole)}`,
			{ method: 'GET' },
		);
		if (response.status === 429) {
			this.backoff.noteRateLimited(parseRetryAfterSeconds(response.headers.get('Retry-After')));
			console.warn(
				`[HttpRelay] receive 429 — backing off for ${this.backoff.delayBeforeNext()}ms`,
			);
			return [];
		}
		if (!response.ok) {
			if (response.status === 404) return [];
			throw new Error(`Relay receive failed: ${response.status}`);
		}
		this.backoff.noteOk();
		return response.json();
	}

	close(): void { /* nothing to clean up */ }
}

// ── Manual Transport (airgapped copy-paste / QR) ─────────────────────

export interface ManualTransportCallbacks {
	onOutboundMessage: (from: string, to: string, body: string) => void;
	/** Called when advanceOutboundHead() empties the outbound queue (show scan / paste UI). */
	onOutboundDrained?: () => void;
}

/** Delay between auto-advance steps in demo mode (ms). */
const DEMO_ADVANCE_MS = 350;

export class ManualTransport implements CosignTransport {
	readonly name = 'Manual (copy-paste)';
	private inbox: Map<string, string[]> = new Map();
	private outQueue: Array<{ from: string; to: string; body: string }> = [];
	private callbacks: ManualTransportCallbacks;
	private injectWaiters: Array<() => void> = [];
	private chunkAccum: Map<string, Map<number, string>> = new Map();
	private demoChannel: BroadcastChannel | null = null;
	private demoRole = '';

	constructor(callbacks: ManualTransportCallbacks) {
		this.callbacks = callbacks;
	}

	/**
	 * Enable demo mode: outbound messages are cross-posted to a BroadcastChannel
	 * so the other ManualTransport in the same browser auto-receives them.
	 * The outbound queue still fills (QRs are displayed) and auto-drains.
	 */
	enableDemoMode(sessionId: string, myRole: string): void {
		if (this.demoChannel) return;
		this.demoRole = myRole;
		this.demoChannel = new BroadcastChannel(`wb32cosign-demo-${sessionId}`);
		this.demoChannel.onmessage = (ev: MessageEvent) => {
			const { to, body } = ev.data || {};
			if (to !== myRole || !body) return;
			this.injectMessage(to, body);
			setTimeout(() => this.autoDrainOutQueue(), DEMO_ADVANCE_MS);
		};
	}

	isDemoModeEnabled(): boolean {
		return !!this.demoChannel;
	}

	private autoDrainOutQueue(): void {
		if (this.outQueue.length === 0) return;
		this.outQueue.shift();
		if (this.outQueue.length > 0) {
			this.emitOutboundHead();
			setTimeout(() => this.autoDrainOutQueue(), DEMO_ADVANCE_MS);
		} else {
			this.callbacks.onOutboundDrained?.();
		}
	}

	private emitOutboundHead(): void {
		const h = this.outQueue[0];
		if (h) this.callbacks.onOutboundMessage(h.from, h.to, h.body);
		else this.callbacks.onOutboundDrained?.();
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		const wasEmpty = this.outQueue.length === 0;
		if (encryptedBody.length <= MAX_QR_CHUNK_BYTES) {
			this.outQueue.push({ from, to, body: encryptedBody });
		} else {
			const dataSize = MAX_QR_CHUNK_BYTES - 12;
			const total = Math.ceil(encryptedBody.length / dataSize);
			for (let i = 0; i < total; i++) {
				const chunk = encryptedBody.slice(i * dataSize, (i + 1) * dataSize);
				this.outQueue.push({ from, to, body: `${CHUNK_PREFIX}${i + 1}:${total}:${chunk}` });
			}
		}
		if (wasEmpty && this.outQueue.length > 0) this.emitOutboundHead();
		if (this.demoChannel) {
			this.demoChannel.postMessage({ from, to, body: encryptedBody });
		}
	}

	/**
	 * Airgap UI: user finished showing the current QR; show the next queued outbound if any.
	 * @returns true if another QR is now active; false if the queue is empty.
	 */
	advanceOutboundHead(): boolean {
		if (this.outQueue.length === 0) return false;
		this.outQueue.shift();
		if (this.outQueue.length > 0) {
			this.emitOutboundHead();
			return true;
		}
		this.callbacks.onOutboundDrained?.();
		return false;
	}

	peekOutboundHead(): string {
		return this.outQueue[0]?.body || '';
	}

	/**
	 * Inject a message (or chunk) from the other party.
	 * @returns true if a complete message is now in the inbox; false if still collecting chunks.
	 */
	injectMessage(to: string, encryptedBody: string): boolean {
		const m = encryptedBody.match(/^M:(\d+):(\d+):(.+)$/s);
		if (m) {
			const part = parseInt(m[1], 10);
			const total = parseInt(m[2], 10);
			const data = m[3];
			const key = `${to}:${total}`;
			if (part === 1) this.chunkAccum.delete(key);
			if (!this.chunkAccum.has(key)) this.chunkAccum.set(key, new Map());
			this.chunkAccum.get(key)!.set(part, data);
			if (this.chunkAccum.get(key)!.size >= total) {
				const parts: string[] = [];
				for (let i = 1; i <= total; i++) parts.push(this.chunkAccum.get(key)!.get(i) || '');
				this.chunkAccum.delete(key);
				const queue = this.inbox.get(to) || [];
				queue.push(parts.join(''));
				this.inbox.set(to, queue);
				this.wakeWaiters();
				return true;
			}
			return false;
		}
		const queue = this.inbox.get(to) || [];
		queue.push(encryptedBody);
		this.inbox.set(to, queue);
		this.wakeWaiters();
		return true;
	}

	async receive(myRole: string): Promise<string[]> {
		const queue = this.inbox.get(myRole) || [];
		if (queue.length === 0) return [];
		this.inbox.set(myRole, []);
		return queue;
	}

	waitForInject(timeoutMs = 120000): Promise<void> {
		return new Promise((resolve, reject) => {
			const timer = setTimeout(() => reject(new Error('Manual transport: timed out waiting for injected message')), timeoutMs);
			this.injectWaiters.push(() => { clearTimeout(timer); resolve(); });
		});
	}

	private wakeWaiters(): void {
		for (const wake of this.injectWaiters) wake();
		this.injectWaiters = [];
	}

	close(): void {
		this.wakeWaiters();
		this.outQueue = [];
		this.chunkAccum.clear();
		if (this.demoChannel) { this.demoChannel.close(); this.demoChannel = null; }
	}
}

// ── Multi-Transport (listens on several, first message wins) ─────────

/**
 * Sends every message to **all** child transports and waits for **all** to settle.
 * WebRTCTransport.send is fail-fast (returns immediately if the data channel
 * isn't open), so it's safe to include alongside HttpRelayTransport here.
 * `createDefaultTransports()` combines BroadcastChannel + WebRTC + HTTP relay
 * and is the recommended way to build a robust online cosign stack.
 */
export class MultiTransport implements CosignTransport {
	readonly name: string;
	private transports: CosignTransport[];

	constructor(transports: CosignTransport[]) {
		this.transports = transports;
		this.name = transports.map(t => t.name).join(' + ');
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		await Promise.allSettled(
			this.transports.map(t => t.send(from, to, encryptedBody)),
		);
	}

	async receive(myRole: string): Promise<string[]> {
		const settled = await Promise.allSettled(
			this.transports.map(t => t.receive(myRole)),
		);
		const all: string[] = [];
		for (const r of settled) {
			if (r.status === 'fulfilled') all.push(...r.value);
		}
		return all;
	}

	close(): void {
		for (const t of this.transports) t.close();
	}
}

// ── WebRTC Transport (peer-to-peer data channel) ─────────────────────

const SIGNAL_POLL_MS = 800;
const ICE_SERVERS: RTCIceServer[] = [
	{ urls: 'stun:stun.l.google.com:19302' },
	{ urls: 'stun:stun1.l.google.com:19302' },
];

interface SignalEnvelope {
	type: 'offer' | 'answer' | 'candidate';
	sdp?: string;
	candidate?: RTCIceCandidateInit;
}

export class WebRTCTransport implements CosignTransport {
	readonly name = 'WebRTC (peer-to-peer)';
	private pc: RTCPeerConnection;
	private dc: RTCDataChannel | null = null;
	private inbox: Map<string, string[]> = new Map();
	private relayUrl: string;
	private sessionId: string;
	private myRole: string;
	private peerRole: string;
	private ready: Promise<void>;
	private resolveReady!: () => void;
	private closed = false;
	private signalPollTimer: ReturnType<typeof setInterval> | null = null;
	private iceCandidateQueue: RTCIceCandidateInit[] = [];
	private remoteDescSet = false;
	private signalBackoff = new RateLimitBackoff();

	/**
	 * Optional callback invoked when the RTC data channel transitions to
	 * 'open'. Used by UI layers (e.g. CosignerApp) to dismiss the
	 * "WebRTC looks blocked" fallback hint once peer-to-peer is actually
	 * working.
	 */
	private onOpen?: () => void;

	constructor(
		sessionId: string,
		relayUrl: string,
		myRole: string,
		opts?: { onOpen?: () => void },
	) {
		this.sessionId = sessionId;
		this.relayUrl = relayUrl.replace(/\/+$/, '');
		this.myRole = myRole;
		this.peerRole = myRole === 'init' ? 'cosign' : 'init';
		this.onOpen = opts?.onOpen;

		this.ready = new Promise<void>(resolve => { this.resolveReady = resolve; });
		this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

		this.pc.onicecandidate = (ev) => {
			if (ev.candidate) {
				this.postSignal({ type: 'candidate', candidate: ev.candidate.toJSON() });
			}
		};

		if (myRole === 'init') {
			this.dc = this.pc.createDataChannel('mpc', { ordered: true });
			this.setupDataChannel(this.dc);
			this.startOffer();
		} else {
			this.pc.ondatachannel = (ev) => {
				this.dc = ev.channel;
				this.setupDataChannel(this.dc);
			};
			this.startAnswer();
		}
	}

	private setupDataChannel(dc: RTCDataChannel): void {
		dc.onopen = () => {
			console.log(`[WebRTC] data channel opened (role=${this.myRole})`);
			this.resolveReady();
			if (this.signalPollTimer) {
				clearInterval(this.signalPollTimer);
				this.signalPollTimer = null;
			}
			try { this.onOpen?.(); } catch { /* listener errors shouldn't break the channel */ }
		};
		dc.onmessage = (ev) => {
			try {
				const msg = JSON.parse(ev.data);
				const { to, body } = msg;
				if (to && body) {
					const queue = this.inbox.get(to) || [];
					queue.push(body);
					this.inbox.set(to, queue);
				}
			} catch { /* skip malformed */ }
		};
		dc.onclose = () => {
			console.log(`[WebRTC] data channel closed (role=${this.myRole})`);
			this.closed = true;
		};
		dc.onerror = (err) => {
			console.warn(`[WebRTC] data channel error (role=${this.myRole}):`, err);
		};
	}

	private async startOffer(): Promise<void> {
		const offer = await this.pc.createOffer();
		await this.pc.setLocalDescription(offer);
		await this.postSignal({ type: 'offer', sdp: offer.sdp });
		this.pollSignals();
	}

	private async startAnswer(): Promise<void> {
		this.pollSignals();
	}

	private async postSignal(envelope: SignalEnvelope): Promise<void> {
		const url = `${this.relayUrl}/message/${this.sessionId}`;
		const body = JSON.stringify({
			session_id: this.sessionId,
			from: `signal-${this.myRole}`,
			to: [`signal-${this.peerRole}`],
			body: JSON.stringify(envelope),
		});
		try {
			await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
		} catch (err) {
			console.warn('[WebRTC] signal post failed:', (err as Error)?.message);
		}
	}

	private pollSignals(): void {
		if (this.signalPollTimer) return;
		this.signalPollTimer = setInterval(() => this.fetchSignals(), SIGNAL_POLL_MS);
		this.fetchSignals();
	}

	private async fetchSignals(): Promise<void> {
		if (this.closed) {
			if (this.signalPollTimer) { clearInterval(this.signalPollTimer); this.signalPollTimer = null; }
			return;
		}
		// Respect any active 429 cooldown — the signal poller fires at
		// `SIGNAL_POLL_MS` (800ms) which is more than enough to trip
		// even gentle relay rate limits if WebRTC never establishes
		// (the user's session reported "hammered the server until it
		// gave a 429 on everything"). Skipping ticks while the backoff
		// window is open lets the relay recover.
		if (this.signalBackoff.delayBeforeNext() > 0) return;
		const url = `${this.relayUrl}/message/${this.sessionId}/signal-${this.myRole}`;
		try {
			const resp = await fetch(url, { method: 'GET' });
			if (resp.status === 429) {
				this.signalBackoff.noteRateLimited(parseRetryAfterSeconds(resp.headers.get('Retry-After')));
				console.warn(
					`[WebRTC] signal poll 429 — backing off for `
					+ `${this.signalBackoff.delayBeforeNext()}ms`,
				);
				return;
			}
			if (!resp.ok) return;
			this.signalBackoff.noteOk();
			const messages: string[] = await resp.json();
			for (const raw of messages) {
				const sig: SignalEnvelope = JSON.parse(raw);
				await this.handleSignal(sig);
			}
		} catch { /* network error, retry next tick */ }
	}

	private async handleSignal(sig: SignalEnvelope): Promise<void> {
		if (sig.type === 'offer' && this.myRole !== 'init') {
			await this.pc.setRemoteDescription({ type: 'offer', sdp: sig.sdp });
			this.remoteDescSet = true;
			for (const c of this.iceCandidateQueue) {
				await this.pc.addIceCandidate(c);
			}
			this.iceCandidateQueue = [];
			const answer = await this.pc.createAnswer();
			await this.pc.setLocalDescription(answer);
			await this.postSignal({ type: 'answer', sdp: answer.sdp });
		} else if (sig.type === 'answer' && this.myRole === 'init') {
			await this.pc.setRemoteDescription({ type: 'answer', sdp: sig.sdp });
			this.remoteDescSet = true;
			for (const c of this.iceCandidateQueue) {
				await this.pc.addIceCandidate(c);
			}
			this.iceCandidateQueue = [];
		} else if (sig.type === 'candidate' && sig.candidate) {
			if (this.remoteDescSet) {
				await this.pc.addIceCandidate(sig.candidate);
			} else {
				this.iceCandidateQueue.push(sig.candidate);
			}
		}
	}

	/** True once the data channel has transitioned to 'open' at least once. */
	isOpen(): boolean {
		return !!this.dc && this.dc.readyState === 'open';
	}

	/** Wait for the data channel to open (or reject after `timeoutMs`). */
	async waitReady(timeoutMs = 20_000): Promise<void> {
		if (this.isOpen()) return;
		const timeout = new Promise<never>((_, reject) =>
			setTimeout(() => reject(new Error('WebRTC data channel not ready within timeout')), timeoutMs),
		);
		await Promise.race([this.ready, timeout]);
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		// Fail-fast: we never block waiting for the data channel to open,
		// so WebRTCTransport is safe to combine with HttpRelayTransport inside
		// MultiTransport — messages go via both pipes opportunistically and the
		// receiver deduplicates by protocol-level invariants (message id, etc).
		if (!this.dc || this.dc.readyState !== 'open') {
			// Silently drop; the HTTP relay sibling (or a later resend) will deliver.
			return;
		}
		try {
			this.dc.send(JSON.stringify({ from, to, body: encryptedBody }));
		} catch (err) {
			console.warn('[WebRTC] send failed:', (err as Error)?.message);
		}
	}

	async receive(myRole: string): Promise<string[]> {
		const queue = this.inbox.get(myRole) || [];
		if (queue.length === 0) return [];
		this.inbox.set(myRole, []);
		return queue;
	}

	close(): void {
		this.closed = true;
		if (this.signalPollTimer) { clearInterval(this.signalPollTimer); this.signalPollTimer = null; }
		if (this.dc) { try { this.dc.close(); } catch { /* */ } }
		try { this.pc.close(); } catch { /* */ }
	}
}

// ── Factory ──────────────────────────────────────────────────────────

export function createTransport(
	type: TransportType,
	sessionId: string,
	relayUrl?: string,
	manualCallbacks?: ManualTransportCallbacks,
	myRole?: string,
): CosignTransport {
	switch (type) {
		case 'broadcastchannel':
			return new BroadcastChannelTransport(sessionId);
		case 'relay':
			if (!relayUrl) throw new Error('Relay transport requires a URL. Run scripts/cosign-relay-server.mjs or provide a relay URL.');
			return new HttpRelayTransport(relayUrl, sessionId);
		case 'manual':
		case 'qr':
			if (!manualCallbacks) throw new Error('ManualTransport requires callbacks');
			return new ManualTransport(manualCallbacks);
		case 'webrtc':
			if (!relayUrl) throw new Error('WebRTC transport requires a relay URL for signalling.');
			return new WebRTCTransport(sessionId, relayUrl, myRole || 'init');
		default:
			throw new Error(`Unknown transport: ${type}`);
	}
}

/**
 * Build the standard transport stack for a participant.
 *
 * Includes (in delivery priority order, but all used in parallel):
 *   1. BroadcastChannel  — same-origin same-device (instant)
 *   2. WebRTC            — peer-to-peer data channel (low latency once open)
 *   3. HTTP relay        — fallback that always works (server-side queue)
 *
 * WebRTC.send is fail-fast (returns immediately if the data channel isn't open)
 * so it's safe to combine with HttpRelay. The HTTP relay acts as the always-on
 * delivery fabric, while WebRTC opportunistically short-circuits once the P2P
 * connection is established.
 */
export function createDefaultTransports(
	sessionId: string,
	relayUrl?: string,
	myRole?: string,
	opts?: { onWebRtcOpen?: () => void },
): CosignTransport {
	const bc = new BroadcastChannelTransport(sessionId);
	if (!relayUrl) return bc;

	const relay = new HttpRelayTransport(relayUrl, sessionId);
	// myRole is required for WebRTC (different offer/answer behaviour for init vs cosign).
	// If unknown, fall back to BC+HTTP only.
	if (!myRole) return new MultiTransport([bc, relay]);

	const webrtc = new WebRTCTransport(sessionId, relayUrl, myRole, { onOpen: opts?.onWebRtcOpen });
	return new MultiTransport([bc, webrtc, relay]);
}
